/** A param: set in the Parameters panel, stored with the drawing, and only read by its script. */
export interface ScriptParam {
  readonly name: string;
  readonly value: number;
  readonly description: string;
}

export type ScriptParams = readonly ScriptParam[];

export const MAX_PARAMS = 200;
const MAX_NAME = 64;
const MAX_DESCRIPTION = 500;
const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
// Names the sandbox's `params` object answers itself, or that JSON reads.
const RESERVED = new Set(['toJSON', '__proto__']);

/** Why `name` cannot name the param at `index`, or null when it can. */
export function paramNameProblem(name: string, params: ScriptParams, index = -1): string | null {
  if (name === '') return 'A param needs a name';
  if (!IDENTIFIER.test(name) || name.length > MAX_NAME || RESERVED.has(name)) {
    return `"${name}" cannot be read as params.${name}; use letters, digits, _ and $, not starting with a digit`;
  }
  const taken = params.findIndex((param) => param.name === name);
  if (taken >= 0 && taken !== index) return `There is already a param named "${name}"`;
  return null;
}

/** Keeps only well-formed params with unique names, from whatever a client sent. */
export function cleanParams(value: unknown): ScriptParams {
  if (!Array.isArray(value)) return [];
  const params: ScriptParam[] = [];
  for (const entry of value as unknown[]) {
    if (params.length === MAX_PARAMS) break;
    if (typeof entry !== 'object' || entry === null) continue;
    const { name, value: number, description } = entry as Record<string, unknown>;
    if (typeof name !== 'string' || paramNameProblem(name, params) !== null) continue;
    if (typeof number !== 'number' || !Number.isFinite(number)) continue;
    params.push({
      name,
      value: number,
      description: typeof description === 'string' ? description.slice(0, MAX_DESCRIPTION) : '',
    });
  }
  return params;
}

export function sameParams(a: ScriptParams, b: ScriptParams): boolean {
  return (
    a.length === b.length &&
    a.every(
      (param, i) =>
        param.name === b[i]!.name &&
        param.value === b[i]!.value &&
        param.description === b[i]!.description,
    )
  );
}

/** The first of `param1`, `param2`, ... that no param uses yet. */
export function nextParamName(params: ScriptParams): string {
  for (let n = params.length + 1; ; n += 1) {
    const name = `param${n}`;
    if (!params.some((param) => param.name === name)) return name;
  }
}

/** Declarations for the code editor, so `params.` completes the drawing's own names. */
export function paramTypes(params: ScriptParams): string {
  const members = params.map(
    ({ name, description }) =>
      `${description ? `  /** ${description.replaceAll('*/', '*\\/')} */\n` : ''}  readonly ${name}: number;`,
  );
  return `/** The drawing's params, set in the Parameters panel; read-only. */
declare const params: {
${members.join('\n')}
};
`;
}
